document.addEventListener('DOMContentLoaded', () => {
  // ─── Header height CSS variable ─────────────────────────────────────────
  const mainHeader = document.getElementById('main-header');
  // Measure the full sticky group (Offers Bar + Header) so --header-height
  // reflects the total pinned area. Scroll tabs use this variable for `top`.
  const headerGroupEl = document.querySelector('.shopify-section-group-header-group');
  const headerDesktopEl = mainHeader ? mainHeader.querySelector('.header-desktop') : null;
  const headerTopBarEl = mainHeader ? mainHeader.querySelector('.header-top-bar') : null;

  let cachedNavHeight = 74;
  let cachedScrollRange = 74;
  let cachedTopPicksHeight = 0;
  let resizeRafId = null;

  // Heights supplied by ResizeObserver — no offsetHeight layout reads needed.
  let cachedHeaderH = 0;
  let cachedTopBarH = 0;
  let cachedBaseNavH = 0;
  const headerEl = mainHeader || headerGroupEl;

  // Pure write function — uses ResizeObserver-supplied cache, never reads the DOM.
  const syncAllHeights = () => {
    if (headerDesktopEl && cachedTopPicksHeight === 0) {
      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const imgWrapH = Math.min(90, Math.max(64, window.innerWidth * 0.0625));
      cachedTopPicksHeight = imgWrapH + Math.round(rem * 0.8125 * 1.35) + Math.round(rem * 1.5);
    }
    if (headerEl && cachedHeaderH) {
      document.documentElement.style.setProperty('--header-height', `${cachedHeaderH}px`);
    }
    if (cachedTopBarH) {
      document.documentElement.style.setProperty('--header-topbar-height', `${cachedTopBarH}px`);
    }
    if (headerDesktopEl && cachedBaseNavH) {
      const oneRem = parseFloat(getComputedStyle(document.documentElement).fontSize);
      const totalNavHeight = cachedBaseNavH + cachedTopPicksHeight + oneRem;
      document.documentElement.style.setProperty('--nav-height', `${totalNavHeight}px`);
      document.documentElement.style.setProperty('--scroll-range', `${totalNavHeight}px`);
      // Actual rendered nav-bar height — used by the home page overlay to reserve
      // space under the fixed nav and as its exact hide distance.
      document.documentElement.style.setProperty('--header-desktop-height', `${cachedBaseNavH}px`);
      cachedNavHeight = totalNavHeight;
      cachedScrollRange = totalNavHeight;
    }
  };

  // ResizeObserver delivers element heights without triggering layout reads.
  // Fires once on observe (initial sizing) and again whenever the element resizes.
  const heightsRO = new ResizeObserver(entries => {
    for (const entry of entries) {
      const h = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
      if (entry.target === headerEl) cachedHeaderH = h;
      else if (entry.target === headerTopBarEl) cachedTopBarH = h;
      else if (entry.target === headerDesktopEl) { cachedBaseNavH = h; cachedTopPicksHeight = 0; }
    }
    syncAllHeights();
  });
  if (headerEl) heightsRO.observe(headerEl);
  if (headerTopBarEl) heightsRO.observe(headerTopBarEl);
  if (headerDesktopEl) heightsRO.observe(headerDesktopEl);

  const initScrollY = window.scrollY;
  window.addEventListener('resize', () => {
    if (resizeRafId) cancelAnimationFrame(resizeRafId);
    resizeRafId = requestAnimationFrame(() => {
      cachedTopPicksHeight = 0;
      syncAllHeights();
      resizeRafId = null;
    });
  }, { passive: true });


  // ─── Mobile drawer ───────────────────────────────────────────────────────
  const trigger = document.getElementById('mobile-menu-trigger');
  const drawer = document.getElementById('mobile-menu-drawer');

  if (trigger && drawer) {
    let isOpen = false;

    const positionDrawer = () => {
      // Measure #mobile-header directly — most reliable on mobile
      const mobileHeaderBar = document.getElementById('mobile-header');
      const ref = mobileHeaderBar || headerGroupEl || mainHeader;
      if (ref) {
        drawer.style.top = ref.getBoundingClientRect().bottom + 'px';
      }
    };

    // Block touchmove on the document behind the drawer to prevent
    // elastic scroll creating a gap between the header and drawer top.
    const blockBodyTouch = (e) => {
      if (!drawer.contains(e.target)) e.preventDefault();
    };

    const openDrawer = () => {
      positionDrawer();
      isOpen = true;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('touchmove', blockBodyTouch, { passive: false });
    };

    const closeDrawer = () => {
      isOpen = false;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('touchmove', blockBodyTouch);
    };

    trigger.addEventListener('click', () => {
      if (isOpen) closeDrawer(); else openDrawer();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) closeDrawer();
    });

    document.addEventListener('click', (e) => {
      if (isOpen && !drawer.contains(e.target) && !trigger.contains(e.target)) {
        closeDrawer();
      }
    });

    // ─── Accordion toggles (level 1 rows and level 2 subrows) ───────────────
    drawer.querySelectorAll('[data-drawer-toggle]').forEach((toggle) => {
      const submenu = toggle.parentElement?.querySelector(':scope > ul');
      if (!submenu) return;

      const plusIcon = toggle.querySelector('.header-drawer__toggle-icon--plus');
      const minusIcon = toggle.querySelector('.header-drawer__toggle-icon--minus');

      const toggleSubmenu = () => {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

        submenu.hidden = isExpanded;
        toggle.setAttribute('aria-expanded', String(!isExpanded));
        if (plusIcon) plusIcon.hidden = !isExpanded;
        if (minusIcon) minusIcon.hidden = isExpanded;
      };

      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleSubmenu();
      });

      // Level 1 triggers are div[role="button"] (their submenu is a sibling inside
      // .header-drawer__row-button, which a real <button> cannot contain), so Enter
      // and Space have to be wired up by hand.
      if (toggle.tagName !== 'BUTTON') {
        toggle.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          e.stopPropagation();
          toggleSubmenu();
        });
      }
    });
  }

  // ─── Sticky header: proportional nav collapse on scroll ──────────────────
  // closeAll / activeMegaDropdown are set later but referenced only at call time, so hoisting is safe.
  let closeAll = null;
  let activeMegaDropdown = null;

  // Lerp factor: how fast currentOffset chases targetOffset each frame (0–1).
  // 0.2 = ~200ms to fully settle — responsive yet smooth for both slow and fast scroll.
  const LERP = 0.2;
  let currentOffset = 0;
  let targetOffset = 0;
  let rafId = null;

  // Animation loop: lerps currentOffset toward targetOffset each frame
  const animateOffset = () => {
    const diff = targetOffset - currentOffset;
    if (Math.abs(diff) < 0.05) {
      currentOffset = targetOffset;
      document.documentElement.style.setProperty('--header-desktop-offset', `${currentOffset}px`);
      rafId = null;
      return;
    }
    currentOffset += diff * LERP;
    document.documentElement.style.setProperty('--header-desktop-offset', `${currentOffset}px`);
    rafId = requestAnimationFrame(animateOffset);
  };

  // Home page uses a directional reveal (hide on scroll down, show on scroll up).
  // Every other page keeps the proportional collapse tied to scroll position.
  const isHome = document.body.classList.contains('index');
  const SCROLL_DELTA = 5; // ignore sub-pixel / jitter scrolls
  let lastScrollY = initScrollY;

  // Scroll handler: uses cachedNavHeight / cachedScrollRange set by syncNavHeight() —
  // no getComputedStyle reads on every scroll event.
  const updateHeaderOffset = () => {
    const scrollY = window.scrollY;
    if (isHome) {
      // Reveal-on-scroll-up: slide the nav up by exactly its own height so it
      // tucks behind the sticky top bar, and bring it straight back on scroll up.
      const hideDistance = cachedBaseNavH || cachedNavHeight;
      const delta = scrollY - lastScrollY;
      if (Math.abs(delta) > SCROLL_DELTA) {
        if (delta > 0 && scrollY > hideDistance) {
          targetOffset = -hideDistance; // scrolling down → hide
        } else if (delta < 0) {
          targetOffset = 0; // scrolling up → show
        }
        lastScrollY = scrollY;
      }
      if (scrollY <= 0) targetOffset = 0; // pinned at the very top → always show
    } else {
      targetOffset = Math.max(-cachedNavHeight, -(scrollY / cachedScrollRange) * cachedNavHeight);
    }
    if (!rafId) rafId = requestAnimationFrame(animateOffset);
    if (!mainHeader) return;
    mainHeader.classList.toggle('shadow-sm', scrollY > 0);
    const wasScrolled = mainHeader.classList.contains('header--scrolled');
    mainHeader.classList.toggle('header--scrolled', scrollY >= cachedScrollRange);
    if (!wasScrolled && scrollY > 10 && closeAll && !activeMegaDropdown) closeAll();
  };

  // ResizeObserver fires synchronously with initial sizing, so cachedNavHeight is already
  // populated before this rAF runs. No syncAllHeights() call needed here.
  requestAnimationFrame(() => {
    // Home starts with the nav shown; other pages use the proportional collapse.
    currentOffset = targetOffset = isHome
      ? 0
      : Math.max(-cachedNavHeight, -(initScrollY / cachedScrollRange) * cachedNavHeight);
    document.documentElement.style.setProperty('--header-desktop-offset', `${currentOffset}px`);
    if (mainHeader) {
      mainHeader.classList.toggle('shadow-sm', initScrollY > 0);
      mainHeader.classList.toggle('header--scrolled', initScrollY >= cachedScrollRange);
      window.addEventListener('scroll', updateHeaderOffset, { passive: true });
    }
  });

  // ─── Desktop nav ──────────────────────────────────────────────────────────
  const navTriggers = [...document.querySelectorAll('[data-nav-trigger]')];
  const navDropdowns = [...document.querySelectorAll('[data-nav-dropdown]')];

  if (navTriggers.length === 0) return;

  let activeIndex = null;

  // ─── Mega dropdown ────────────────────────────────────────────────────────
  const openMegaDropdown = (dropdown) => {
    if (dropdown._megaCloseListener) {
      dropdown.removeEventListener('transitionend', dropdown._megaCloseListener);
      dropdown._megaCloseListener = null;
    }
    dropdown.classList.add('mega-dropdown--open');
    dropdown.removeAttribute('hidden');
    // Double-rAF: first frame paints the --open state; second frame adds --visible so the
    // clip-path transition has a committed from-state. Replaces getBoundingClientRect() forced reflow.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        dropdown.classList.add('mega-dropdown--visible');
      });
    });
    activeMegaDropdown = dropdown;
  };

  const closeMegaDropdown = (dropdown) => {
    activeMegaDropdown = null;
    if (!dropdown.classList.contains('mega-dropdown--visible')) {
      dropdown.classList.remove('mega-dropdown--open');
      dropdown.setAttribute('hidden', '');
      return;
    }
    dropdown.classList.remove('mega-dropdown--visible');
    const onClose = (e) => {
      if (e.propertyName !== 'clip-path') return;
      dropdown.removeEventListener('transitionend', onClose);
      dropdown._megaCloseListener = null;
      dropdown.classList.remove('mega-dropdown--open');
      dropdown.setAttribute('hidden', '');
    };
    dropdown._megaCloseListener = onClose;
    dropdown.addEventListener('transitionend', onClose);
  };

  const closeAllDropdowns = () => {
    navTriggers.forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
      t.closest('.header-desktop__item')?.classList.remove('is-active');
    });
    navDropdowns.forEach((d) => {
      if (d.classList.contains('mega-dropdown--open')) {
        closeMegaDropdown(d);
      } else {
        d.setAttribute('hidden', '');
      }
    });
    activeIndex = null;
  };

  closeAll = closeAllDropdowns;

  navTriggers.forEach((navTrigger) => {
    navTrigger.addEventListener('click', (e) => {
      e.stopPropagation();

      const index = navTrigger.dataset.navTrigger;

      if (activeIndex === index) {
        closeAllDropdowns();
        return;
      }

      closeAllDropdowns();

      const dropdown = navDropdowns.find((d) => d.dataset.navDropdown === index);

      if (dropdown) {
        openMegaDropdown(dropdown);
        navTrigger.setAttribute('aria-expanded', 'true');
        navTrigger.closest('.header-desktop__item')?.classList.add('is-active');
        activeIndex = index;
      }
    });
  });

  document.addEventListener('click', (e) => {
    if (activeIndex !== null && !e.target.closest('.header-desktop')) {
      closeAllDropdowns();
    }
  });

});
